import React, { useCallback, useMemo } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import Container from '~/components/atoms/Container/Container'
import Loader from '~/components/atoms/Loader/Loader'
import type { ProfileImageProps } from '~/components/atoms/ProfileImage/ProfileImage'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { rearrangeNameBySurname } from '~/utils/rearrangeNameBySurname'
import { getFirstLetter, getLastWord } from '~/utils/sortArrayKeyByFirstWord'
import {
  groupArtistsByLastNameLetter,
  sortArrayKeyByLastWords,
} from '~/utils/sortArrayKeyByLastWord'

import type { ProcessedProps } from '../Catalogue/Catalogue'

import type { IArtistListItem } from './hooks'
import useHooks from './hooks'

import styles from './ArtistsList.module.css'

export function organiseArtistsIntoSections(
  itemsList: any,
  renderComponent: any
) {
  const sortedItems = sortArrayKeyByLastWords(
    itemsList,
    'name'
  ) as ProfileImageProps[]

  const processedData: any = []
  let lastSectionIndex = 0

  sortedItems?.map(item => {
    // Get the first letter of surname/alias
    const sectionFirstLetter = getFirstLetter(getLastWord(item.name))

    if (
      !processedData.some(function (obj: any) {
        return obj['section'] === sectionFirstLetter
      })
    ) {
      const itemsObj = [] as any

      sortedItems.map((item, idx) => {
        const itemFirstLetter = getFirstLetter(getLastWord(item.name))
        if (sectionFirstLetter === itemFirstLetter) {
          itemsObj.push({ ...item, index: idx + 1 })
        }
      })

      // New letter so create the section
      processedData[lastSectionIndex] = {
        section: sectionFirstLetter,
        items: itemsObj,
      }
      lastSectionIndex++
    }
  })

  return (
    <div>
      {processedData.map((cat: ProcessedProps) => {
        return (
          <div key={cat.section} className={styles.section}>
            <Text
              Tag="h4"
              size={TextSizes.primaryTitleMed}
              className={styles.sectionTitle}
            >
              {cat.section}
            </Text>

            {cat.items?.map((item: any, idx: any) =>
              renderComponent({ item, key: `mobile-artist-item-${idx}` })
            )}
          </div>
        )
      })}
    </div>
  )
}

export interface ArtistsListProps {
  fallbackData: any
  querySlug?: string
}

const ArtistListItem = ({ artist, bySurname }: any) => {
  const t = useTranslations('common')
  const countType = t('works')
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMediumMax})`)

  return (
    <Link
      href={`/${artist.qualifiedUrl.startsWith('artist-') ? artist.qualifiedUrl : `artist-${artist.qualifiedUrl}`}`}
      className={classNames(styles.artistItem, styles.simpleArtistItem)}
      prefetch={false}
    >
      <>
        <Text
          size={TextSizes.primaryTitleSml}
          className={classNames(
            styles.simpleArtistName,
            isMobile && styles.displayBlock
          )}
        >
          {bySurname ? rearrangeNameBySurname(artist.name) : artist.name}
        </Text>
        {artist?.totalArtworks > 0 && (
          <Text
            size={TextSizes.tertiaryTitleSml}
            Tag="p"
            className={styles.tags}
          >
            {artist?.totalArtworks}{' '}
            {artist?.totalArtworks === 1
              ? countType?.substring(0, countType?.length - 1)
              : countType}
          </Text>
        )}
      </>
    </Link>
  )
}

export default function ArtistsList({
  fallbackData,
  querySlug,
}: ArtistsListProps) {
  const router = useRouter()
  const searchQuery = router.query?.searchTerm
  const { data, isLoading } = useHooks(
    fallbackData,
    (searchQuery as string) || ''
  )
  let title = ''

  const dataFiltered = useMemo(() => {
    const dataToProcess = data
    if (!Array.isArray(dataToProcess)) return []

    const featuredOnly =
      router.query?.featured === 'true' && !router.query?.searchTerm
    const allArtistsFiltered = featuredOnly
      ? dataToProcess.filter(
          (item: any) => item?.totalArtworks !== null || item?.totalArtworks > 0
        )
      : dataToProcess
    return allArtistsFiltered
  }, [data, router.query?.featured, router.query?.searchTerm])

  if (querySlug) title = `Artists / ${querySlug}`

  /**
   * Renders the desktop specific view
   */
  const renderDesktopView = useCallback(() => {
    const sortedItems = sortArrayKeyByLastWords(
      dataFiltered,
      'name'
    ) as IArtistListItem[]

    const groupedItems = groupArtistsByLastNameLetter(sortedItems)

    // fet count of groupItems with items > 0
    const groupCount = groupedItems.filter(
      group => group.items.length > 0
    ).length

    if (groupCount === 0 && !isLoading) {
      return (
        <div className={styles.noResults}>
          <Text size={TextSizes.primaryTitleMed}>No results found</Text>
        </div>
      )
    }

    return (
      <div className={styles.artistList}>
        {!isLoading &&
          groupedItems.map(
            group =>
              group.items.length > 0 && (
                <div
                  key={group.letter}
                  className={styles.letterGroup}
                  id={`artists-${group.letter?.toLowerCase()}`}
                >
                  <div className={styles.letterGroupTitle}>
                    <Text
                      className={styles.groupTitle}
                      size={TextSizes.primaryTitleMed}
                    >
                      {group.letter}
                    </Text>
                  </div>
                  <div className={styles.artistItems}>
                    {group.items.map((artist: IArtistListItem, i: number) => {
                      return (
                        <ArtistListItem
                          key={`artist-list-item-${group.letter}-${i}`}
                          artist={artist}
                          bySurname
                        />
                      )
                    })}
                  </div>
                </div>
              )
          )}
      </div>
    )
  }, [dataFiltered, isLoading])

  return (
    <div className={styles.base}>
      <Container>
        {isLoading && <Loader />}
        {renderDesktopView()}
      </Container>
    </div>
  )
}
